import { useCallback, useRef, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { TRACKING_EVENTS } from '../utils/trackingEvents';

// Genere un ID de session unique pour cette instance de l'app
const generateSessionId = () => {
  const stored = sessionStorage.getItem('tracking_session_id');
  if (stored) return stored;

  const newId = crypto.randomUUID ? crypto.randomUUID() :
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });

  sessionStorage.setItem('tracking_session_id', newId);
  return newId;
};

/**
 * Hook de tracking des evenements utilisateur
 *
 * @example
 * const { track, trackPageView, trackAction, trackAudit } = useTracking();
 *
 * // Tracker une vue de page
 * trackPageView('planning');
 *
 * // Tracker une action
 * trackAction('prestation_created', 'prestation', 42, { status: 'draft' });
 *
 * // Tracker un changement (audit)
 * trackAudit('user', 5, { name: 'Old' }, { name: 'New' });
 */
export const useTracking = () => {
  const sessionId = useRef(generateSessionId());
  const eventQueue = useRef([]);
  const flushTimeout = useRef(null);

  // Recupere le token a chaque appel pour eviter les closures stale
  const getToken = useCallback(() => localStorage.getItem('token'), []);

  // Envoie les evenements en batch
  const flushEvents = useCallback(async () => {
    if (eventQueue.current.length === 0) return;

    const token = getToken();
    // Ne pas envoyer si pas de token (utilisateur déconnecté)
    if (!token) {
      eventQueue.current = []; // Vider la queue silencieusement
      return;
    }

    const eventsToSend = [...eventQueue.current];
    eventQueue.current = [];

    try {
      await fetch(API_ENDPOINTS.ANALYTICS_TRACK_BATCH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          events: eventsToSend,
          sessionId: sessionId.current
        })
      });
    } catch (error) {
      // Silencieux - le tracking ne doit pas bloquer l'UX
      if (process.env.NODE_ENV === 'development') {
        console.warn('Tracking batch failed:', error);
      }
    }
  }, [getToken]);

  // Programme un flush apres delai (batching)
  const scheduleFlush = useCallback(() => {
    if (flushTimeout.current) {
      clearTimeout(flushTimeout.current);
    }
    flushTimeout.current = setTimeout(flushEvents, 2000); // 2s debounce
  }, [flushEvents]);

  // Flush a la fermeture de la page
  useEffect(() => {
    const handleUnload = () => {
      const token = getToken();
      // Ne pas envoyer si pas de token (utilisateur déconnecté)
      if (eventQueue.current.length > 0 && navigator.sendBeacon && token) {
        const blob = new Blob([JSON.stringify({
          events: eventQueue.current,
          sessionId: sessionId.current,
          token // Note: sendBeacon ne supporte pas les headers, le token est dans le body
        })], { type: 'application/json' });
        navigator.sendBeacon(API_ENDPOINTS.ANALYTICS_TRACK_BATCH, blob);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (flushTimeout.current) {
        clearTimeout(flushTimeout.current);
      }
    };
  }, [getToken]);

  /**
   * Fonction generique de tracking
   */
  const track = useCallback((eventName, data = {}) => {
    const event = {
      eventType: data.eventType || 'custom',
      eventName,
      category: data.category || 'analytics',
      module: data.module,
      entityType: data.entityType,
      entityId: data.entityId,
      entityLabel: data.entityLabel,
      metadata: data.metadata || {},
      oldValues: data.oldValues,
      newValues: data.newValues,
      durationMs: data.durationMs
    };

    eventQueue.current.push(event);
    scheduleFlush();
  }, [scheduleFlush]);

  /**
   * Track une vue de page/module
   */
  const trackPageView = useCallback((module, metadata = {}) => {
    track(TRACKING_EVENTS.PAGE_VIEW, {
      eventType: 'page_view',
      module,
      metadata: {
        ...metadata,
        url: window.location.pathname,
        referrer: document.referrer
      }
    });
  }, [track]);

  /**
   * Track une action utilisateur (clic, creation, modification...)
   */
  const trackAction = useCallback((actionName, entityType, entityId, metadata = {}) => {
    track(actionName, {
      eventType: 'action',
      entityType,
      entityId,
      metadata
    });
  }, [track]);

  /**
   * Track une consultation d'entité (view) pour l'audit
   */
  const trackView = useCallback((eventName, entityType, entityId, entityLabel, metadata = {}) => {
    track(eventName, {
      category: 'audit',
      eventType: 'view',
      entityType,
      entityId,
      entityLabel,
      metadata
    });
  }, [track]);

  /**
   * Track un changement pour l'audit trail
   */
  const trackAudit = useCallback((entityType, entityId, oldValues, newValues, metadata = {}) => {
    track(TRACKING_EVENTS.ENTITY_UPDATED, {
      category: 'audit',
      eventType: 'entity_update',
      entityType,
      entityId,
      oldValues,
      newValues,
      metadata
    });
  }, [track]);

  /**
   * Track une erreur
   */
  const trackError = useCallback((errorName, metadata = {}) => {
    track(errorName, {
      category: 'perf',
      eventType: 'error',
      metadata: {
        ...metadata,
        url: window.location.pathname,
        timestamp: new Date().toISOString()
      }
    });
  }, [track]);

  return {
    track,
    trackPageView,
    trackAction,
    trackView,
    trackAudit,
    trackError,
    sessionId: sessionId.current
  };
};

export default useTracking;
